import { useState } from "react";
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { TESTIMONIALS, RECOMMENDATIONS_URL, BENEFITS } from "@/data/portfolio";
import Benefit from "@/components/site/Benefit";
import { track } from "@/lib/analytics";
import { T, useLang } from "@/lib/i18n";


function Card({ t, i, featured }) {
  const { t: tr } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <figure
      data-testid={`testimonial-${i}`}
      className={`flex flex-col ${
        featured ? "p-6 bg-white" : "p-5 bg-white/60"
      } border border-[#e5e0d8]`}
    >
      <blockquote
        className={
          featured
            ? "font-display italic text-lg sm:text-xl leading-snug text-[#14352a]"
            : "text-sm italic text-[#14352a] leading-relaxed"
        }
      >
        &ldquo;{t.pull}&rdquo;
      </blockquote>

      {open && (
        <p
          data-testid={`testimonial-full-${i}`}
          className="mt-5 pt-5 border-t border-[#e5e0d8] text-sm text-[#55625b] leading-relaxed whitespace-pre-line"
        >
          {t.quote}
        </p>
      )}

      <button
        data-testid={`testimonial-expand-${i}`}
        onClick={() => setOpen((v) => !v)}
        className="mt-4 self-start inline-flex items-center gap-1 font-mono-label text-[10px] text-[#9a7218] link-underline"
      >
        {open ? tr("Hide full recommendation") : tr("Read full recommendation")}
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      <figcaption className="mt-6 flex items-center gap-3">
        <span className="w-8 h-8 shrink-0 rounded-full bg-[#9a7218] text-white grid place-items-center font-display text-sm">
          {t.name.charAt(0)}
        </span>
        <div>
          <div className="text-sm text-[#14352a] font-medium">{t.name}</div>
          <div className="font-mono-label text-[10px] text-[#86928a]">
            {t.role}
          </div>
          <div className="font-mono-label text-[9px] text-[#b0b6bd] mt-0.5">
            {tr(t.relation.split(" · ")[0])}
          </div>
        </div>
      </figcaption>
    </figure>
  );
}

export default function Testimonials() {
  const { t } = useLang();
  const [showAll, setShowAll] = useState(false);
  const featured = TESTIMONIALS.filter((t) => t.featured);
  const rest = TESTIMONIALS.filter((t) => !t.featured);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="border-t border-[#e5e0d8] section-pad bg-[#f2ece4]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="font-mono-label text-[11px] text-[#9a7218]">
              {t("08 · In their words")}
            </div>
            <p className="mt-3 text-sm text-[#55625b] leading-relaxed">
              {t("Verbatim LinkedIn recommendations. Nothing paraphrased, the pull-quotes are sentences from the originals.")}
            </p>
          </div>
          <a
            data-testid="testimonials-linkedin-link"
            href={RECOMMENDATIONS_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => track("external_link", { label: "linkedin-recommendations" })}
            className="inline-flex items-center gap-1 font-mono-label text-[11px] text-[#14352a] link-underline"
          >
            Verify on LinkedIn <ArrowUpRight className="h-3 w-3" />
          </a>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {featured.map((t, i) => (
            <Card key={t.name} t={t} i={i} featured />
          ))}
        </div>

        {showAll && (
          <div
            data-testid="testimonials-extra"
            className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {rest.map((t, i) => (
              <Card key={t.name} t={t} i={featured.length + i} />
            ))}
          </div>
        )}

        <Benefit text={BENEFITS.testimonials} testid="benefit-testimonials" />

        <div className="mt-6 text-center">
          <button
            data-testid="testimonials-toggle-btn"
            onClick={() => setShowAll((v) => !v)}
            className="font-mono-label text-[11px] text-[#9a7218] link-underline"
          >
            {showAll ? t(", Show less") : `${t("+ Show more")} (${rest.length})`}
          </button>
        </div>
      </div>
    </section>
  );
}
